import axios from 'axios';
import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '';

// Convert snake_case keys to camelCase recursively
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

function convertKeysToCamelCase(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(convertKeysToCamelCase);
  }
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      result[snakeToCamel(key)] = convertKeysToCamelCase(value);
    }
    return result;
  }
  return obj;
}

// Convert camelCase keys to snake_case for outgoing requests
function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function convertKeysToSnakeCase(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(convertKeysToSnakeCase);
  }
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      result[camelToSnake(key)] = convertKeysToSnakeCase(value);
    }
    return result;
  }
  return obj;
}

const client = axios.create({
  baseURL: `${API_URL}/api/v1`,
  withCredentials: true,
  // Без таймаута зависший запрос держит Promise.allSettled в fetchAll вечно →
  // бесконечный спиннер на главной. 8с — потолок, дальше запрос падает с ошибкой.
  timeout: 8000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// In-memory access token storage (NOT localStorage for security)
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

// Request interceptor: attach access token + convert keys to snake_case
client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    if (config.data && typeof config.data === 'object') {
      config.data = convertKeysToSnakeCase(config.data);
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor: convert keys to camelCase + handle 401
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
}

client.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data) {
      response.data = convertKeysToCamelCase(response.data);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Don't trigger refresh for auth endpoints themselves — a 401 from /auth/login
    // means wrong password, not an expired session. Refreshing would fail with
    // its own 401 (no refresh cookie) and mask the real error in the UI.
    const url = originalRequest.url || '';
    const isAuthEndpoint =
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/forgot-password') ||
      url.includes('/auth/reset-password');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      // Помечаем сразу: повторный 401 после рефреша НЕ должен запускать
      // новый цикл рефреша (иначе шторм refresh→retry→refresh = вечная загрузка).
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              if (token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              resolve(client(originalRequest));
            },
            reject,
          });
        });
      }

      isRefreshing = true;

      try {
        // ВАЖНО: timeout обязателен. Это голый axios (не client), и без таймаута
        // зависший на мобильной сети refresh держит isRefreshing=true навсегда →
        // вся очередь 401-запросов не оседает → вечный спиннер на главной.
        const response = await axios.post(
          `${API_URL}/api/v1/auth/refresh`,
          {},
          { withCredentials: true, timeout: 15000 }
        );

        const converted = convertKeysToCamelCase(response.data) as { accessToken?: string };
        const newToken = converted.accessToken || '';
        // Рефреш без токена — это провал, а не успех: не уходим в повторную
        // попытку с пустым токеном (она бы снова дала 401 по кругу).
        if (!newToken) {
          throw new Error('refresh returned no access token');
        }
        setAccessToken(newToken);
        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return client(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        setAccessToken(null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default client;
